import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Building2 className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading font-semibold text-foreground">Turizm Finans</div>
              <div className="text-[11px] text-foreground/50 tracking-wider uppercase">Nakit & Cari Kontrol</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl tracking-tight text-foreground font-medium">Hoş geldin.</h1>
          <p className="text-sm text-foreground/60 mt-2 mb-10">
            Şirket ortakları için nakit akışı ve cari hesap kontrol paneli.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-foreground/60">E-posta</Label>
              <Input
                id="email" type="email" required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="ornek@sirket.com"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-foreground/60">Şifre</Label>
              <Input
                id="password" type="password" required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
                className="h-11"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90">
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>

            <div className="text-sm text-foreground/60 text-center pt-4">
              Hesabın yok mu?{" "}
              <Link to="/kayit" className="text-primary underline underline-offset-4 hover:text-primary/80" data-testid="link-to-register">
                Kayıt Ol
              </Link>
            </div>
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-secondary">
        <img src={HERO_IMG} alt="Modern architecture" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent" />
      </div>
    </div>
  );
}
